import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  catchError,
  finalize,
  from,
  map,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { SkeletonModule } from 'primeng/skeleton';

import { FileUploadService } from '../../services/file-upload.service';
import { NotificationService } from '../../services/notification.service';
import { UserService } from '../../services/user.service';
import {
  PROFILE_IMAGE_SIZE,
  resizeImageToSquare,
} from 'src/app/utils/image-resize';
import { AvatarBadge, initialsAndColor } from 'src/app/utils/avatar';
import { NgStyle } from '@angular/common';

// Auch die Navbar zeigt dieses Bild, wenn kein Profilbild hinterlegt ist
export const DEFAULT_AVATAR = 'assets/default-avatar.png';

const BUCKET_NAME = 'user-profile-images';

// Gilt für die Originaldatei, nicht für das verkleinerte Ergebnis: PrimeNG prüft die Größe
// beim Auswählen, resizeImageToSquare() läuft erst beim Hochladen.
const MAX_FILE_SIZE = 2 * 1024 * 1024;

@Component({
  selector: 'app-profile-image-editor',
  standalone: true,
  imports: [
    NgStyle,
    ButtonModule,
    DialogModule,
    FileUploadModule,
    SkeletonModule,
  ],
  templateUrl: './profile-image-editor.component.html',
  styleUrl: './profile-image-editor.component.scss',
})
export class ProfileImageEditorComponent implements OnChanges {
  @ViewChild('profileUpload') profileUpload?: FileUpload;

  // Die bereits aufgelöste presigned URL bzw. das Default-Avatar. null = noch nicht
  // aufgelöst; dann wird ein Platzhalter gezeigt statt eines fremden Bildes.
  @Input() imageUrl: string | null = null;
  @Input() userId = 0;
  // Für den Initialen-Avatar, solange kein eigenes Bild hinterlegt ist
  @Input() displayName = '';

  // Der neue MinIO-Object-Key; null bedeutet, dass das Bild entfernt wurde.
  @Output() imageChanged = new EventEmitter<string | null>();

  readonly maxFileSize = MAX_FILE_SIZE;
  readonly maxFileSizeLabel = `${MAX_FILE_SIZE / (1024 * 1024)} MB`;
  readonly maxFileSizeMessage = `Die Datei darf höchstens ${this.maxFileSizeLabel} groß sein.`;
  readonly profileImageSize = PROFILE_IMAGE_SIZE;
  // Fürs Template: entscheidet Initialen vs. <img>
  readonly DEFAULT_AVATAR = DEFAULT_AVATAR;

  displayDialog = false;
  uploadingImage = false;
  removingImage = false;

  // Gesetzt, wenn das Laden der presigned URL im Browser scheitert
  private imageFailed = false;

  constructor(
    private fileUploadService: FileUploadService,
    private userService: UserService,
    private notificationService: NotificationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Eine neue URL bekommt eine neue Chance, sonst bliebe das Default-Avatar kleben
    if (changes['imageUrl']) {
      this.imageFailed = false;
    }
  }

  get displayUrl(): string | null {
    return this.imageFailed ? DEFAULT_AVATAR : this.imageUrl;
  }

  get avatarBadge(): AvatarBadge {
    return initialsAndColor(this.displayName);
  }

  // Greift, wenn die presigned URL abgelaufen ist oder die Datei im Bucket fehlt - MinIO
  // signiert auch nicht existierende Objekte anstandslos, der Fehler zeigt sich erst hier.
  onImageError(): void {
    // Ohne diese Prüfung entstünde eine Endlosschleife, falls auch das Asset fehlt
    if (this.imageUrl !== DEFAULT_AVATAR) {
      this.imageFailed = true;
    }
  }

  showBildDialog(): void {
    this.displayDialog = true;
  }

  hideBildDialog(): void {
    this.displayDialog = false;
  }

  uploadImage(event: { files: File[] }): void {
    const files = event.files;

    if (!this.userId || !files?.length) return;

    // PrimeNGs Upload-Button richtet sich allein danach, ob eine Datei ausgewählt ist -
    // das [disabled] der Komponente erreicht ihn nicht. Ohne diese Sperre startet jeder
    // weitere Klick einen zweiten Upload.
    if (this.uploadingImage) return;

    this.uploadingImage = true;

    // Vor dem Upload proportional auf Quadratformat herunterrechnen - dadurch landen
    // weder Originalauflösungen im Bucket noch wird beim Anzeigen etwas abgeschnitten.
    from(resizeImageToSquare(files[0], PROFILE_IMAGE_SIZE))
      .pipe(
        switchMap((resized) =>
          this.fileUploadService.uploadToMinio([resized], BUCKET_NAME),
        ),
        switchMap((urls: string[]) => this.saveProfileImageKey(urls?.[0])),
        finalize(() => (this.uploadingImage = false)),
      )
      .subscribe({
        next: (newProfileImageUrl) => {
          this.notificationService.success('Profilbild aktualisiert');
          // Ohne clear() bliebe die hochgeladene Datei in der Liste stehen und
          // ließe sich beim nächsten Öffnen des Dialogs erneut hochladen.
          this.profileUpload?.clear();
          this.hideBildDialog();
          this.imageChanged.emit(newProfileImageUrl);
        },
        // Deckt Formatprüfung, Upload und Speichern ab; jeder Schritt liefert eine
        // eigene Meldung.
        error: (err) =>
          this.notificationService.error(
            err?.message ?? 'Das Bild konnte nicht hochgeladen werden.',
            err,
          ),
      });
  }

  // Hinterlegt den Object-Key am Benutzer und gibt ihn weiter, damit der Aufrufer nach
  // dem Speichern noch weiß, welches Bild es geworden ist.
  private saveProfileImageKey(key: string | undefined): Observable<string> {
    if (!key) {
      return throwError(
        () => new Error('Der Upload hat keinen Dateischlüssel geliefert.'),
      );
    }

    return this.userService.updateProfileImageUrl(this.userId, key).pipe(
      map(() => key),
      catchError((err) =>
        throwError(
          () =>
            new Error('Profilbild konnte nicht aktualisiert werden.', {
              cause: err,
            }),
        ),
      ),
    );
  }

  removeImage(): void {
    if (!this.userId || this.removingImage) return;

    this.removingImage = true;

    this.userService
      .updateProfileImageUrl(this.userId, null)
      .pipe(finalize(() => (this.removingImage = false)))
      .subscribe({
        next: () => {
          this.notificationService.info('Profilbild wurde entfernt');
          this.imageChanged.emit(null);
        },
        error: (err) =>
          this.notificationService.error(
            'Profilbild konnte nicht entfernt werden',
            err,
          ),
      });
  }
}
